import { stringify } from "querystring"
import { Trans } from "@lingui/macro"
import { Button, Modal, ModalBody } from "@silversea-ssc/ui-components-react"
import React, { useCallback } from "react"
import { Occupancy } from "@ssc-booking/api-contracts"
import { useRouter } from "next/router"
import { SanityDestination } from "../../app-core/_common/sanity/sanity-types"
import { websiteUrl } from "../../app-core/_common/localizeWebsiteUrl"
import { formatTotalOccupancyValue } from "../../app-core/_common/format/occupancy"
import { homeV3, removeFromQuery } from "../../app-core/_common/routing"
import styles from "./NoAvailabilityBox.module.scss"

interface Props {
    cruiseCode: string
    destination: SanityDestination
    occupancy: Occupancy
}

const NoAvailabilityBox: React.FC<React.PropsWithChildren<Props>> = ({ cruiseCode, destination, occupancy }) => {
    const router = useRouter()

    const queryWithoutCruiseCode = removeFromQuery(router.query, "cruiseCode", "guests")
    const queryUrl = stringify(queryWithoutCruiseCode)
    const goChangeGuests = useCallback(() => {
        location.href = `${homeV3(cruiseCode)}?${queryUrl}`
    }, [cruiseCode, queryUrl])

    const goFindYourCruise = useCallback(() => {
        location.href = websiteUrl(destination.path)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [destination.name, destination.path])

    const goRequestQuote = useCallback(() => {
        location.href = `//www.silversea.com/request-quote-s/${cruiseCode}`
    }, [cruiseCode])

    const totalOccupancy = formatTotalOccupancyValue(occupancy)

    return (
        <Modal modal={{ isOpen: true }} size="small" dataContentName="message_modal">
            <ModalBody variant="boxed" className={styles.modalBodyWrapper}>
                <div className={styles.modalContent}>
                    <div className={styles.title}>
                        <Trans id="no-suites-available-title">Sorry, no suites available for {totalOccupancy}</Trans>
                    </div>
                    <div className={styles.body}>
                        <Trans id="no-suites-available-content">
                            <h3>What can I do now?</h3>
                            <ul>
                                <li>Discover other {destination.name} cruises</li>
                                <li>
                                    Send a request to our Reservation Team who will help find the perfect solution for
                                    you
                                </li>
                                <li>
                                    <button className={styles.changeGuests} onClick={goChangeGuests}>
                                        Change the number of guests
                                    </button>
                                </li>
                            </ul>
                        </Trans>
                    </div>

                    <div className={styles.buttonsContainer}>
                        <Button variant="text" onClick={goFindYourCruise} dataContentPiece="other_cruises">
                            <Trans>Other {destination.name} cruises</Trans>
                        </Button>
                        <Button variant="primary" onClick={goRequestQuote} dataContentPiece="request-a-quote">
                            <Trans>request a custom quote</Trans>
                        </Button>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    )
}

export default NoAvailabilityBox
